import { ArrowLeft, ExternalLink, Mail, MapPin, Phone, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import TalentOsCandidateMultiSelect from '../components/TalentOsCandidateMultiSelect.js';
import {
  parseTalentOsAssignments,
  useCompanyPerson,
  useCreateCompanyPersonActivity,
  useDraftRecruiterCandidateOutreach,
  useTalentOsActiveCandidates,
  useUpdateCompanyPerson,
} from '../hooks/use-api.js';
import type { TalentOsActiveCandidate } from '../hooks/use-api.js';
import type {
  CompanyPersonCandidateConversationEntry,
  CompanyPersonCandidateDetail,
  CompanyPersonCandidateStatus,
} from '../api.js';

type RecruiterOutreachTemplateKey = 'same_day_application' | 'follow_up' | 'new_introduction';

const RECRUITER_OUTREACH_TEMPLATES: Array<{
  value: RecruiterOutreachTemplateKey;
  label: string;
}> = [
  { value: 'same_day_application', label: 'Same-Day Application' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'new_introduction', label: 'New Introduction' },
];

const CANDIDATE_STATUS_OPTIONS: Array<{
  value: CompanyPersonCandidateStatus;
  label: string;
}> = [
  { value: 'not_contacted', label: 'Not contacted' },
  { value: 'connected', label: 'Connected' },
  { value: 'message_sent', label: 'Message sent' },
  { value: 'replied', label: 'Replied' },
  { value: 'follow_up', label: 'Follow-up' },
];

function json(value: unknown): string {
  if (!value) return '—';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// Candidate ids are always real UUIDs. This guards against ever rendering a
// malformed stored value (e.g. a JSON blob from a since-fixed backend bug)
// as if it were a candidate card — such a value is silently dropped here
// rather than displayed, instead of failing loudly on bad data every CRM
// user would see.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function buildCandidateDetails(
  person: {
    talentos_candidate_id?: string | null;
    talentos_candidate_name?: string | null;
    talentos_candidate_details?: CompanyPersonCandidateDetail[] | null;
  },
  candidates: Array<{ id: string; name: string }>
): CompanyPersonCandidateDetail[] {
  const existing = Array.isArray(person.talentos_candidate_details)
    ? person.talentos_candidate_details
    : [];
  const ids = parseTalentOsAssignments(person.talentos_candidate_id);
  const names = parseTalentOsAssignments(person.talentos_candidate_name);
  const selectedIds = (ids.length ? ids : existing.map((item) => item.candidate_id)).filter((id) =>
    UUID_PATTERN.test(id)
  );
  return selectedIds.map((candidateId, index) => {
    const previous = existing.find((item) => item.candidate_id === candidateId);
    const candidate = candidates.find((item) => item.id === candidateId);
    if (previous) {
      return { ...previous, history: previous.history ?? [] };
    }
    return {
      candidate_id: candidateId,
      candidate_name: candidate?.name ?? names[index] ?? candidateId,
      status: 'not_contacted',
      initial_outreach_message: '',
      conversation_summary: '',
      history: [],
      template_key: null,
      application_id: null,
      generated_at: null,
    };
  });
}

function updateCandidateDetail(
  details: CompanyPersonCandidateDetail[],
  candidateId: string,
  patch: Partial<CompanyPersonCandidateDetail>
): CompanyPersonCandidateDetail[] {
  return details.map((detail) =>
    detail.candidate_id === candidateId ? { ...detail, ...patch } : detail
  );
}

function candidateApplicationContext(
  candidate: TalentOsActiveCandidate | undefined,
  selectedApplicationId?: string | null
): string {
  if (!candidate) return '';
  const applications = selectedApplicationId
    ? (candidate.applications ?? []).filter(
        (application) => application.id === selectedApplicationId
      )
    : (candidate.applications ?? []);
  return applications
    .map((application) => {
      const status = String(application.application_status || '')
        .replaceAll('_', ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
      const job = [application.job_title, application.job_company].filter(Boolean).join(' — ');
      const value =
        application.applied_at ??
        application.ae_applied_at ??
        application.application_created_at ??
        application.job_posted_at;
      const date = value ? new Date(value) : null;
      const dateLabel = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : '';
      return [status, job || 'Job details unavailable', dateLabel].filter(Boolean).join(' · ');
    })
    .join(' | ');
}

function candidateField(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).filter(Boolean).join(', ');
  return typeof value === 'string' ? value.trim() : '';
}

function applicationDate(application: TalentOsActiveCandidate['applications'][number]): string {
  const value =
    application.applied_at ??
    application.ae_applied_at ??
    application.application_created_at ??
    application.job_posted_at;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : '';
}

export default function CompanyPersonDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useCompanyPerson(id);
  const createActivity = useCreateCompanyPersonActivity();
  const updatePerson = useUpdateCompanyPerson();
  const draftRecruiterOutreach = useDraftRecruiterCandidateOutreach();
  const {
    data: candidateData,
    isLoading: candidatesLoading,
    error: candidatesError,
  } = useTalentOsActiveCandidates();
  const [note, setNote] = useState('');
  const [subject, setSubject] = useState('');
  const [type, setType] = useState('note');
  const [candidateDetails, setCandidateDetails] = useState<CompanyPersonCandidateDetail[]>([]);
  const [historyDrafts, setHistoryDrafts] = useState<Record<string, string>>({});
  const [editingHistory, setEditingHistory] = useState<{
    candidateId: string;
    entryId: string;
  } | null>(null);
  const [editingHistoryText, setEditingHistoryText] = useState('');
  const [outreachErrors, setOutreachErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!data?.person) return;
    setCandidateDetails(buildCandidateDetails(data.person, candidateData?.candidates ?? []));
  }, [data?.person?.id, data?.person?.updated_at, candidateData?.candidates.length]);

  if (isLoading) return <div className="text-slate-500">Loading profile…</div>;
  if (!data?.person) return <div className="text-slate-500">Profile not found.</div>;
  const p = data.person;
  const persistCandidateDetails = (next: CompanyPersonCandidateDetail[]) => {
    setCandidateDetails(next);
    updatePerson.mutate({ id, talentosCandidateDetails: next });
  };
  const handleCandidateSelection = (ids: string[]) => {
    const current = candidateDetails.length
      ? candidateDetails
      : buildCandidateDetails(p, candidateData?.candidates ?? []);
    const next = ids.map((candidateId) => {
      const existing = current.find((detail) => detail.candidate_id === candidateId);
      const candidate = candidateData?.candidates.find((item) => item.id === candidateId);
      if (existing) {
        return { ...existing, history: existing.history ?? [] };
      }
      return {
        candidate_id: candidateId,
        candidate_name: candidate?.name ?? candidateId,
        status: 'not_contacted' as const,
        initial_outreach_message: '',
        conversation_summary: '',
        history: [],
        template_key: null,
        application_id: null,
        generated_at: null,
      };
    });
    persistCandidateDetails(next);
  };
  const updateDraftDetail = (candidateId: string, patch: Partial<CompanyPersonCandidateDetail>) => {
    setCandidateDetails((current) => updateCandidateDetail(current, candidateId, patch));
  };
  const saveHistoryEntry = (candidateId: string, entryId: string, noteText: string) => {
    const next = updateCandidateDetail(candidateDetails, candidateId, {
      history: (
        candidateDetails.find((detail) => detail.candidate_id === candidateId)?.history ?? []
      ).map((entry) =>
        entry.id === entryId
          ? { ...entry, note: noteText.trim(), updated_at: new Date().toISOString() }
          : entry
      ),
    });
    persistCandidateDetails(next);
    setEditingHistory(null);
  };
  const addHistoryEntry = (candidateId: string) => {
    const noteText = (historyDrafts[candidateId] ?? '').trim();
    if (!noteText) return;
    const entry: CompanyPersonCandidateConversationEntry = {
      id: crypto.randomUUID(),
      note: noteText,
      occurred_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const detail = candidateDetails.find((item) => item.candidate_id === candidateId);
    if (!detail) return;
    persistCandidateDetails(
      updateCandidateDetail(candidateDetails, candidateId, {
        history: [...(detail.history ?? []), entry],
      })
    );
    setHistoryDrafts((current) => ({ ...current, [candidateId]: '' }));
  };
  const generateOutreachMessage = (
    detail: CompanyPersonCandidateDetail,
    candidate: TalentOsActiveCandidate | undefined
  ) => {
    if (!candidate) return;
    const templateKey = detail.template_key;
    if (!templateKey) {
      setOutreachErrors((current) => ({
        ...current,
        [detail.candidate_id]: 'Choose an outreach template first.',
      }));
      return;
    }
    const selectedApplicationId =
      (detail.application_id &&
      candidate.applications.some((application) => application.id === detail.application_id)
        ? detail.application_id
        : null) ||
      candidate.applications?.[0]?.id ||
      null;
    if (templateKey !== 'new_introduction' && !selectedApplicationId) {
      setOutreachErrors((current) => ({
        ...current,
        [detail.candidate_id]: 'Select an eligible application for this template first.',
      }));
      return;
    }
    setOutreachErrors((current) => ({ ...current, [detail.candidate_id]: '' }));
    draftRecruiterOutreach.mutate(
      {
        personId: id,
        candidateId: detail.candidate_id,
        applicationId: selectedApplicationId,
        templateKey,
      },
      {
        onSuccess: (result) => {
          const formatted = `Subject: ${result.draft.subject}\n\n${result.draft.body}`;
          const next = updateCandidateDetail(candidateDetails, detail.candidate_id, {
            initial_outreach_message: formatted,
            application_id: result.application?.id ?? selectedApplicationId,
            template_key: templateKey,
            generated_at: new Date().toISOString(),
          });
          persistCandidateDetails(next);
        },
        onError: (error) => {
          setOutreachErrors((current) => ({
            ...current,
            [detail.candidate_id]:
              error instanceof Error ? error.message : 'Unable to generate the draft.',
          }));
        },
      }
    );
  };
  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft size={16} /> Back
      </button>
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-indigo-100 p-3 text-indigo-700">
            <UserRound />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-slate-900">{p.display_name}</h1>
              {p.person_number && (
                <span
                  className="rounded bg-slate-100 px-2 py-0.5 font-mono text-sm text-slate-600"
                  title="Recruiter tracking number"
                >
                  {p.person_number}
                </span>
              )}
            </div>
            <p className="text-slate-600">{p.current_title ?? p.headline ?? 'Role not captured'}</p>
            <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-500">
              {p.location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={14} />
                  {p.location}
                </span>
              )}
              {p.phone && (
                <span className="inline-flex items-center gap-1">
                  <Phone size={14} />
                  {p.phone}
                </span>
              )}
              {p.linkedin_url && (
                <a
                  className="inline-flex items-center gap-1 text-indigo-600"
                  href={p.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink size={14} />
                  LinkedIn
                </a>
              )}
              {/* Always rendered: an absent address is itself information the
                  operator needs before trying to reach this contact. */}
              {p.email ? (
                <a
                  className="inline-flex items-center gap-1 text-indigo-600"
                  href={`mailto:${p.email}`}
                >
                  <Mail size={14} />
                  {p.email}
                </a>
              ) : (
                <span className="inline-flex items-center gap-1 text-slate-400">
                  <Mail size={14} />
                  Email not found
                </span>
              )}
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div className="font-semibold text-indigo-700">
              {p.data_completeness ?? 0}% complete
            </div>
            <div className="mt-1">
              Last captured{' '}
              {p.last_captured_at ? new Date(p.last_captured_at).toLocaleString() : '—'}
            </div>
            <div>Key: {p.captured_by_api_key_label ?? '—'}</div>
          </div>
        </div>
      </section>
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="font-semibold">Outreach on behalf of</h2>
        <p className="mt-1 text-xs text-slate-500">
          Only active candidates with an Applied, Screening, Interview, or Offer application logged
          in the last 15 days are listed. Select several candidates and they will appear as tags.
        </p>
        <TalentOsCandidateMultiSelect
          candidates={candidateData?.candidates ?? []}
          selectedIds={
            candidateDetails.length
              ? candidateDetails.map((detail) => detail.candidate_id)
              : parseTalentOsAssignments(p.talentos_candidate_id)
          }
          disabled={candidatesLoading || updatePerson.isPending}
          loading={candidatesLoading}
          error={Boolean(candidatesError)}
          ariaLabel="TalentOS candidates for this contact"
          onChange={handleCandidateSelection}
        />
        {candidatesError && !candidatesLoading ? (
          <p className="mt-2 text-xs text-rose-600">
            TalentOS candidate data is currently unavailable. Refresh the page to retry.
          </p>
        ) : candidateData?.candidates.length === 0 && !candidatesLoading ? (
          <p className="mt-2 text-xs text-amber-600">No active TalentOS candidates found.</p>
        ) : null}
      </section>
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Candidate conversations</h2>
            <p className="mt-1 text-xs text-slate-500">
              Track outreach separately for every candidate assigned to this contact.
            </p>
          </div>
          <span className="text-xs text-slate-500">{candidateDetails.length} candidates</span>
        </div>
        {candidateDetails.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-400">
            Select one or more candidates above to start tracking their conversations.
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {candidateDetails.map((detail) => {
              const candidate = candidateData?.candidates.find(
                (item) => item.id === detail.candidate_id
              );
              const resolvedApplicationId =
                (detail.application_id &&
                candidate?.applications.some(
                  (application) => application.id === detail.application_id
                )
                  ? detail.application_id
                  : null) ||
                candidate?.applications?.[0]?.id ||
                null;
              const selectedApplication =
                candidate?.applications.find(
                  (application) => application.id === resolvedApplicationId
                ) ?? candidate?.applications?.[0];
              const context = candidateApplicationContext(candidate, selectedApplication?.id);
              return (
                <article
                  key={detail.candidate_id}
                  className="rounded-lg border border-slate-200 p-4"
                >
                  {candidate && (
                    <div className="mb-4 rounded-xl border border-slate-700/80 bg-slate-900 px-4 py-4 text-sm text-slate-200 shadow-inner">
                      <div className="font-semibold text-white">
                        {[
                          candidate.candidate_number == null
                            ? ''
                            : `Candidate #${candidate.candidate_number}`,
                          candidate.email,
                          candidate.phone,
                        ]
                          .filter(Boolean)
                          .join(' · ') || 'Candidate contact details unavailable'}
                      </div>
                      <div className="mt-1.5 leading-6 text-slate-300">{context}</div>
                      {(candidateField(candidate.target_roles) ||
                        candidateField(candidate.target_industries) ||
                        candidate.location_preference ||
                        candidate.work_mode_preference ||
                        candidate.available_start_date) && (
                        <div className="mt-2 leading-6 text-slate-300">
                          {[
                            candidateField(candidate.target_roles) &&
                              `Roles: ${candidateField(candidate.target_roles)}`,
                            candidateField(candidate.target_industries) &&
                              `Industries: ${candidateField(candidate.target_industries)}`,
                            candidate.location_preference &&
                              `Location: ${candidate.location_preference}`,
                            candidate.work_mode_preference &&
                              `Work mode: ${candidate.work_mode_preference}`,
                            candidate.available_start_date &&
                              `Available: ${candidate.available_start_date}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      )}
                      {selectedApplication && (
                        <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-700/80 pt-3">
                          {selectedApplication.talentos_resume_url && (
                            <a
                              href={selectedApplication.talentos_resume_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 rounded-lg border border-indigo-400/40 bg-indigo-500/15 px-3 py-2 text-sm font-semibold text-indigo-200 transition-colors hover:border-indigo-300/70 hover:bg-indigo-500/25 focus:outline-none focus:ring-2 focus:ring-indigo-400/60"
                            >
                              <ExternalLink size={15} /> Open tailored resume
                            </a>
                          )}
                          {selectedApplication.talentos_job_url && (
                            <a
                              href={selectedApplication.talentos_job_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-100 transition-colors hover:border-slate-500 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            >
                              <ExternalLink size={15} /> Open job in TalentOS
                            </a>
                          )}
                          {!selectedApplication.talentos_resume_url && (
                            <span className="text-sm text-slate-400">
                              No tailored resume version linked
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="font-semibold text-slate-800">{detail.candidate_name}</h3>
                    <label className="flex items-center gap-2 text-xs text-slate-500">
                      Status
                      <select
                        value={detail.status}
                        disabled={updatePerson.isPending}
                        onChange={(event) => {
                          const next = updateCandidateDetail(
                            candidateDetails,
                            detail.candidate_id,
                            {
                              status: event.target.value as CompanyPersonCandidateStatus,
                            }
                          );
                          persistCandidateDetails(next);
                        }}
                        className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
                      >
                        {CANDIDATE_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="mt-4 rounded border border-slate-100 bg-slate-50 p-3">
                    <div className="grid gap-3 md:grid-cols-[1fr_1.4fr_auto] md:items-end">
                      <label className="text-xs text-slate-500">
                        Outreach template
                        <select
                          value={detail.template_key ?? ''}
                          disabled={updatePerson.isPending || draftRecruiterOutreach.isPending}
                          onChange={(event) =>
                            updateDraftDetail(detail.candidate_id, {
                              template_key: event.target.value as RecruiterOutreachTemplateKey,
                            })
                          }
                          className="mt-1 w-full rounded border border-slate-200 bg-white px-2 py-2 text-sm text-slate-700"
                        >
                          <option value="">Choose a template</option>
                          {RECRUITER_OUTREACH_TEMPLATES.map((template) => (
                            <option key={template.value} value={template.value}>
                              {template.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs text-slate-500">
                        Application context
                        <select
                          value={resolvedApplicationId ?? ''}
                          disabled={updatePerson.isPending || draftRecruiterOutreach.isPending}
                          onChange={(event) =>
                            updateDraftDetail(detail.candidate_id, {
                              application_id: event.target.value || null,
                            })
                          }
                          className="mt-1 w-full rounded border border-slate-200 bg-white px-2 py-2 text-sm text-slate-700"
                        >
                          <option value="">Most recent eligible application</option>
                          {(candidate?.applications ?? []).map((application) => (
                            <option key={application.id} value={application.id}>
                              {[
                                application.job_title,
                                application.job_company,
                                applicationDate(application),
                              ]
                                .filter(Boolean)
                                .join(' · ') || 'Eligible application'}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        disabled={
                          !candidate || updatePerson.isPending || draftRecruiterOutreach.isPending
                        }
                        onClick={() => generateOutreachMessage(detail, candidate)}
                        className="rounded bg-indigo-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
                      >
                        {draftRecruiterOutreach.isPending ? 'Generating…' : 'Generate with AI'}
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-slate-400">
                      Choose a scenario first. The draft uses this candidate’s current TalentOS
                      resume and selected job description.
                    </p>
                    {outreachErrors[detail.candidate_id] && (
                      <p className="mt-2 text-xs text-rose-600">
                        {outreachErrors[detail.candidate_id]}
                      </p>
                    )}
                    {detail.generated_at && (
                      <p className="mt-1 text-xs text-emerald-600">
                        Generated {new Date(detail.generated_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-400">
                          Initial outreach message
                        </label>
                        <button
                          type="button"
                          disabled={!detail.initial_outreach_message || updatePerson.isPending}
                          onClick={() =>
                            persistCandidateDetails(
                              updateCandidateDetail(candidateDetails, detail.candidate_id, {
                                initial_outreach_message: '',
                              })
                            )
                          }
                          className="text-xs text-slate-400 hover:text-slate-700 disabled:opacity-40"
                        >
                          Clear
                        </button>
                      </div>
                      <textarea
                        value={detail.initial_outreach_message}
                        disabled={updatePerson.isPending}
                        onChange={(event) =>
                          updateDraftDetail(detail.candidate_id, {
                            initial_outreach_message: event.target.value,
                          })
                        }
                        placeholder="Write or paste the first outreach message…"
                        className="mt-1 min-h-28 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-400">
                        What the conversation is about
                      </label>
                      <textarea
                        value={detail.conversation_summary}
                        disabled={updatePerson.isPending}
                        onChange={(event) =>
                          updateDraftDetail(detail.candidate_id, {
                            conversation_summary: event.target.value,
                          })
                        }
                        placeholder="Summarize the role, interest, questions, or next step…"
                        className="mt-1 min-h-28 w-full rounded border border-slate-200 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={updatePerson.isPending}
                    onClick={() => persistCandidateDetails(candidateDetails)}
                    className="mt-3 rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                  >
                    Save candidate details
                  </button>
                  <div className="mt-5 border-t border-slate-100 pt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-slate-700">Conversation history</h4>
                      <span className="text-xs text-slate-400">
                        {(detail.history ?? []).length} entries
                      </span>
                    </div>
                    <div className="mt-3 space-y-3">
                      {[...(detail.history ?? [])].reverse().map((entry) => {
                        const editing =
                          editingHistory?.candidateId === detail.candidate_id &&
                          editingHistory.entryId === entry.id;
                        return (
                          <div
                            key={entry.id}
                            className="rounded border border-slate-100 bg-slate-50 p-3"
                          >
                            <div className="flex items-center justify-between gap-2 text-xs text-slate-400">
                              <span>{new Date(entry.occurred_at).toLocaleString()}</span>
                              {!editing && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingHistory({
                                      candidateId: detail.candidate_id,
                                      entryId: entry.id,
                                    });
                                    setEditingHistoryText(entry.note);
                                  }}
                                  className="text-indigo-600 hover:underline"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                            {editing ? (
                              <div className="mt-2 space-y-2">
                                <textarea
                                  value={editingHistoryText}
                                  onChange={(event) => setEditingHistoryText(event.target.value)}
                                  className="min-h-20 w-full rounded border border-slate-200 bg-white px-2 py-2 text-sm"
                                />
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    disabled={!editingHistoryText.trim() || updatePerson.isPending}
                                    onClick={() =>
                                      saveHistoryEntry(
                                        detail.candidate_id,
                                        entry.id,
                                        editingHistoryText
                                      )
                                    }
                                    className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingHistory(null)}
                                    className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">
                                {entry.note}
                              </p>
                            )}
                          </div>
                        );
                      })}
                      {(detail.history ?? []).length === 0 && (
                        <p className="text-sm text-slate-400">No conversation entries yet.</p>
                      )}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <textarea
                        value={historyDrafts[detail.candidate_id] ?? ''}
                        disabled={updatePerson.isPending}
                        onChange={(event) =>
                          setHistoryDrafts((current) => ({
                            ...current,
                            [detail.candidate_id]: event.target.value,
                          }))
                        }
                        placeholder="Add the latest conversation update…"
                        className="min-h-20 flex-1 rounded border border-slate-200 px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        disabled={
                          !historyDrafts[detail.candidate_id]?.trim() || updatePerson.isPending
                        }
                        onClick={() => addHistoryEntry(detail.candidate_id)}
                        className="h-fit rounded bg-slate-800 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
                      >
                        Add entry
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold">Profile evidence</h2>
          <div className="mt-4 space-y-4 text-sm">
            <div>
              <div className="text-xs font-medium uppercase text-slate-400">About</div>
              <p className="mt-1 whitespace-pre-wrap text-slate-700">{p.about ?? '—'}</p>
            </div>
            <div>
              <div className="text-xs font-medium uppercase text-slate-400">Experience</div>
              <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap text-xs text-slate-700">
                {json(p.experience)}
              </pre>
            </div>
            <div>
              <div className="text-xs font-medium uppercase text-slate-400">Education / skills</div>
              <pre className="mt-1 whitespace-pre-wrap text-xs text-slate-700">
                {json(p.education)}
                {p.skills ? `\n${json(p.skills)}` : ''}
              </pre>
            </div>
          </div>
        </section>
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold">Capture history</h2>
          <p className="mt-1 text-xs text-slate-500">
            Every extension capture is retained for audit, deduplication, and future AI agents.
          </p>
          <div className="mt-4 divide-y divide-slate-100">
            {data.captures.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <div className="font-medium text-slate-700">
                    {c.captured_by_api_key_label ?? 'API capture'}
                  </div>
                  <div className="text-xs text-slate-500">
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <span className="font-mono text-[11px] text-slate-400">
                  {c.payload_hash?.slice(0, 12) ?? 'snapshot'}
                </span>
              </div>
            ))}
            {data.captures.length === 0 && (
              <div className="py-6 text-sm text-slate-400">No capture snapshots yet.</div>
            )}
          </div>
        </section>
      </div>
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Notes & interactions</h2>
          <span className="text-xs text-slate-500">{data.activities.length} logged</span>
        </div>
        <div className="mt-4 grid gap-2 md:grid-cols-[150px_1fr]">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="rounded border border-slate-200 px-2 py-2 text-sm"
          >
            <option value="note">Note</option>
            <option value="email">Email</option>
            <option value="linkedin_outreach">LinkedIn</option>
            <option value="call">Call</option>
            <option value="meeting">Meeting</option>
            <option value="follow_up">Follow-up</option>
          </select>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (optional)"
            className="rounded border border-slate-200 px-3 py-2 text-sm"
          />
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add context, response, referral, or next step…"
            className="min-h-24 rounded border border-slate-200 px-3 py-2 text-sm md:col-span-2"
          />
          <button
            disabled={createActivity.isPending || (!note.trim() && !subject.trim())}
            onClick={() =>
              createActivity.mutate(
                { id, type, subject, notes: note },
                {
                  onSuccess: () => {
                    setNote('');
                    setSubject('');
                  },
                }
              )
            }
            className="w-fit rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            Log interaction
          </button>
        </div>
        <div className="mt-5 divide-y divide-slate-100">
          {data.activities.map((a) => (
            <div key={a.id} className="py-3">
              <div className="flex justify-between">
                <span className="font-medium text-slate-700">
                  {a.subject ?? a.type.replace('_', ' ')}
                </span>
                <span className="text-xs text-slate-400">
                  {new Date(a.occurred_at).toLocaleString()}
                </span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{a.notes ?? '—'}</p>
            </div>
          ))}
          {data.activities.length === 0 && (
            <div className="py-6 text-sm text-slate-400">No interactions logged yet.</div>
          )}
        </div>
      </section>
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="font-semibold">Employment history</h2>
        <div className="mt-3 divide-y divide-slate-100">
          {data.employments.map((e, i) => (
            <div key={String(e.id ?? i)} className="py-3">
              <div className="font-medium">
                {String(e.company_name ?? e.companyNameSnapshot ?? 'Company')}
              </div>
              <div className="text-sm text-slate-600">
                {String(e.title ?? '—')} · {e.is_current ? 'Current' : 'Past'}
              </div>
            </div>
          ))}
          {data.employments.length === 0 && (
            <div className="text-sm text-slate-400">No employment records linked.</div>
          )}
        </div>
      </section>
      <Link to="/companies" className="text-sm text-indigo-600 hover:underline">
        View linked companies
      </Link>
    </div>
  );
}
